import React, { useEffect, useState } from 'react';
import { Row, Col, Layout, Typography, Space, Button, Image, Flex } from 'antd';
import { useAppSelector } from '../../../hooks/use-app-selector';
import {
  CloseCircleTwoTone,
  DollarTwoTone,
  TabletTwoTone,
  TeamOutlined,
  CreditCardOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import { t } from '../../../utils/i18n';
import { CDN_BASE_URL } from '../../../constants';
import { useGlobals } from '../../../globals-context';
import {
  paymentErrors,
  useInitiatePayPalSetup,
  useInitiateShorePayOnlineAccountSetup,
} from '../../../shared/hooks/payments';
import useMerchantSettings from '../../../hooks/use-merchant-settings';
import { PaymentCaptureTaxIDModal } from './PaymentCaptureTaxIDModal';

const benefitListItemStyle = {
  borderRadius: '35px',
  padding: '10px 15px',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(0,0,0,.1)',
};

const StyledAdvantageCard = styled('div')`
  height: 100%;
  border-radius: 12px;
  background-color: #b3f1ec;
  padding: 40px 30px;
`;

const StyledAdvantageCardIconContainer = styled('div')`
  width: 55px;
  height: 55px;
  padding: 15px;
  border-radius: 12px;
  background-color: #5860d6;
`;

const paypalIcon = (
  <svg
    fill="none"
    viewBox="0 0 62 18"
    style={{
      margin: '0',
      width: '1em',
      height: '1em',
      fontSize: '5rem',
    }}
  >
    <path
      fill="white"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.67187 0.723633H2.88946C2.56215 0.723633 2.28382 0.962385 2.2327 1.28688L0.29846 13.5997C0.260069 13.8428 0.447519 14.0617 0.692946 14.0617H2.97603C3.30333 14.0617 3.58167 13.8231 3.63279 13.498L4.1544 10.177C4.20474 9.85187 4.48366 9.61312 4.81037 9.61312H6.32427C9.47448 9.61312 11.2926 8.08247 11.7676 5.04949C11.9815 3.72239 11.7766 2.67987 11.1576 1.94965C10.4779 1.14784 9.27234 0.723633 7.67187 0.723633ZM8.22332 5.22079C7.96183 6.94378 6.65066 6.94378 5.38298 6.94378H4.66119L5.16752 3.72593C5.19769 3.53143 5.36535 3.38826 5.56122 3.38826H5.89205C6.75565 3.38826 7.57028 3.38826 7.99121 3.88248C8.24232 4.17728 8.3191 4.61545 8.22332 5.22079Z"
    />
    <path
      fill="white"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M22.0894 5.0718H19.7992C19.6041 5.0718 19.4355 5.21497 19.4055 5.40948L19.3041 6.05257L19.144 5.81952C18.6483 5.09697 17.5426 4.85547 16.4392 4.85547C13.9084 4.85547 11.7469 6.78004 11.326 9.47967C11.1072 10.8262 11.4182 12.114 12.1792 13.012C12.8771 13.8376 13.8757 14.1818 15.0636 14.1818C17.1028 14.1818 18.2334 12.8653 18.2334 12.8653L18.1314 13.5042C18.093 13.7485 18.2804 13.9676 18.5245 13.9676H20.5872C20.9153 13.9676 21.1923 13.7288 21.244 13.4037L22.4817 5.53377C22.5209 5.29148 22.334 5.0718 22.0894 5.0718ZM18.897 9.54713C18.6761 10.8607 17.6378 11.7425 16.3133 11.7425C15.6483 11.7425 15.1169 11.5283 14.7755 11.1226C14.437 10.7196 14.3085 10.146 14.416 9.50701C14.6225 8.20469 15.6782 7.29393 16.9826 7.29393C17.6329 7.29393 18.1615 7.51105 18.5098 7.92011C18.8586 8.3337 18.9971 8.91092 18.897 9.54713Z"
    />
    <path
      fill="white"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M34.1102 5.26953H31.8087C31.5891 5.26953 31.3829 5.37907 31.2585 5.56237L28.0844 10.2566L26.7389 5.74546C26.6543 5.46325 26.395 5.26953 26.1012 5.26953H23.8398C23.5648 5.26953 23.374 5.53916 23.4614 5.79896L25.9964 13.2679L23.6132 16.6459C23.4258 16.9119 23.6146 17.2777 23.9384 17.2777H26.2369C26.4551 17.2777 26.6592 17.1709 26.783 16.9914L34.4373 5.89788C34.6206 5.63258 34.4324 5.26953 34.1102 5.26953Z"
    />
    <path
      fill="white"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M41.7823 0.723633H36.9993C36.6728 0.723633 36.3944 0.962385 36.3433 1.28688L34.4091 13.5997C34.3707 13.8428 34.5581 14.0617 34.8022 14.0617H37.2566C37.4844 14.0617 37.6795 13.8947 37.7152 13.6672L38.2642 10.177C38.3146 9.85187 38.5937 9.61312 38.9202 9.61312H40.4335C43.5843 9.61312 45.4018 8.08247 45.8774 5.04949C46.092 3.72239 45.8858 2.67987 45.2668 1.94965C44.5877 1.14784 43.3829 0.723633 41.7823 0.723633ZM42.3341 5.22079C42.0732 6.94378 40.762 6.94378 39.4936 6.94378H38.7728L39.2797 3.72593C39.3097 3.53143 39.4761 3.38826 39.6726 3.38826H40.0034C40.8662 3.38826 41.6817 3.38826 42.1026 3.88248C42.3537 4.17728 42.4299 4.61545 42.3341 5.22079Z"
    />
    <path
      fill="white"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M56.1968 5.0718H53.908C53.7116 5.0718 53.5445 5.21497 53.5151 5.40948L53.4137 6.05257L53.2529 5.81952C52.7571 5.09697 51.6522 4.85547 50.5486 4.85547C48.018 4.85547 45.8573 6.78004 45.4364 9.47967C45.2182 10.8262 45.5279 12.114 46.2886 13.012C46.9881 13.8376 47.9851 14.1818 49.1732 14.1818C51.2123 14.1818 52.343 12.8653 52.343 12.8653L52.241 13.5042C52.2026 13.7485 52.3898 13.9676 52.6355 13.9676H54.6976C55.0241 13.9676 55.3025 13.7288 55.3536 13.4037L56.5919 5.53377C56.6297 5.29148 56.4422 5.0718 56.1968 5.0718ZM53.0046 9.54713C52.785 10.8607 51.7451 11.7425 50.4206 11.7425C49.757 11.7425 49.2242 11.5283 48.8828 11.1226C48.5446 10.7196 48.4172 10.146 48.5234 9.50701C48.7312 8.20469 49.7856 7.29393 51.0899 7.29393C51.7402 7.29393 52.2689 7.51105 52.6171 7.92011C52.9673 8.3337 53.1058 8.91092 53.0046 9.54713Z"
    />
    <path
      fill="white"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M58.7059 1.06209L56.743 13.6003C56.7047 13.8434 56.8919 14.0623 57.136 14.0623H59.1094C59.4375 14.0623 59.7156 13.8237 59.7659 13.4986L61.7016 1.18639C61.7401 0.943308 61.5527 0.723633 61.3086 0.723633H59.099C58.9039 0.724223 58.736 0.867592 58.7059 1.06209Z"
    />
  </svg>
);

export function PaymentSetupTrialMerchantCTAButtons({
  onUpgradeContractClick,
}: {
  onUpgradeContractClick: () => void;
}) {
  return (
    <Flex gap="middle" wrap>
      <Button
        size="large"
        type="primary"
        onClick={onUpgradeContractClick}
        style={{
          backgroundColor: '#6970da',
          fontSize: '1.4rem',
          minWidth: '20rem',
        }}
      >
        {t(
          'application-settings.payments.onboarding-page.introduction.cta.upgrade-plan'
        )}
      </Button>
    </Flex>
  );
}

export function PaymentSetupPaidMerchantCTAButtons({
  isWhitelabel,
  isLoadingPaypalSetup,
  isLoadingShorePayOnlineSetup,
  isPayPalFeatureToggleEnabled,
  onLegacyStripeButtonClick,
  onShorePayOnlineAccountButtonClick,
  onPayPalButtonClick,
}: {
  isWhitelabel: boolean;
  isLoadingPaypalSetup: boolean;
  isLoadingShorePayOnlineSetup: boolean;
  isPayPalFeatureToggleEnabled: boolean;
  onLegacyStripeButtonClick: () => void;
  onShorePayOnlineAccountButtonClick: () => void;
  onPayPalButtonClick: () => void;
}) {
  return (
    <Flex gap="middle" wrap>
      {isWhitelabel && (
        <Button
          size="large"
          type="primary"
          style={{ backgroundColor: '#6970da', fontSize: '1.4rem' }}
          onClick={onLegacyStripeButtonClick}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: t(
                'application-settings.payments.onboarding-page.introduction.cta.card-payment'
              ),
            }}
          />
        </Button>
      )}
      {!isWhitelabel && (
        <Button
          size="large"
          type="primary"
          style={{ backgroundColor: '#6970da', fontSize: '1.4rem' }}
          loading={isLoadingShorePayOnlineSetup}
          onClick={onShorePayOnlineAccountButtonClick}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: t(
                'application-settings.payments.onboarding-page.introduction.cta.card-payment'
              ),
            }}
          />
        </Button>
      )}
      {isPayPalFeatureToggleEnabled && (
        <Button
          size="large"
          type="primary"
          loading={isLoadingPaypalSetup}
          style={{ backgroundColor: '#09f', fontSize: '1.4rem' }}
          onClick={onPayPalButtonClick}
        >
          {t(
            'application-settings.payments.onboarding-page.introduction.cta.paypal-payment'
          )}
          {paypalIcon}
        </Button>
      )}
    </Flex>
  );
}

export function PaymentOnboardingPage() {
  const navigate = useNavigate();
  const tools = useGlobals() as any;
  const { trackMethod, eventBus } = tools;

  const [isCaptureTaxIDModalOpen, setIsCaptureTaxIDModalOpen] = useState(false);
  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const { merchantSettings } = useMerchantSettings();
  const { merchantId, trialDaysLeft, featureToggles } = merchantSettings || {};

  const isTrialMerchant = (trialDaysLeft || 0) > 0;

  const isWhitelabel = currentMerchant.whiteLabel !== null;

  const isPayPalFeatureToggleEnabled =
    featureToggles?.paypal_payment_enabled || false;

  const {
    mutate: mutateInitiatePayPalSetup,
    isLoading: isLoadingInitiatePayPalSetup,
  } = useInitiatePayPalSetup();

  const {
    mutate: mutateInitiateShorePayOnlineAccountSetup,
    isLoading: isLoadingInitiateShorePayOnlineAccountSetup,
  } = useInitiateShorePayOnlineAccountSetup();

  const handleOnUpgradeContractClick = () => {
    trackMethod('application-settings-app_choose_plan', {
      days_trial_left: trialDaysLeft,
    });
    navigate(`/contract`);
  };

  const handleOnLegacyStripeButtonClick = () => {
    trackMethod(
      'application-settings-app_online_payment_initial_screen_setup_legacy_stripe_clicked'
    );
  };

  const handleOnShorePayOnlineAccountButtonClick = () => {
    trackMethod(
      'application-settings-app_online_payment_initial_screen_setup_shore_pay_online_account_clicked'
    );
    mutateInitiateShorePayOnlineAccountSetup(
      {
        merchantId,
        company: merchantSettings?.company,
      },
      {
        onError: (error: unknown) => {
          if (error instanceof Error) {
            if (
              error.message.includes(paymentErrors.vatIdOrTaxNumberRequired)
            ) {
              setIsCaptureTaxIDModalOpen(true);
            }
          }
        },
      }
    );
  };

  const handleOnPayPalButtonClick = () => {
    trackMethod('online_payment_initial_screen_setup_paypal_clicked');
    mutateInitiatePayPalSetup({ merchantId });
  };

  const handleOnCaptureTaxIDModalClose = () => {
    setIsCaptureTaxIDModalOpen(false);
  };

  const handleOnCaptureTaxIDModalFormSubmit = ({
    taxId,
  }: {
    taxId: string;
  }) => {
    mutateInitiateShorePayOnlineAccountSetup(
      {
        taxId,
        merchantId,
        company: merchantSettings?.company,
      },
      {
        onSuccess: () => setIsCaptureTaxIDModalOpen(false),
      }
    );
  };

  useEffect(() => {
    const handlePromotePayOnlineActivated = (data: any) => {
      if (data?.source === 'banner') {
        mutateInitiateShorePayOnlineAccountSetup(
          {
            merchantId,
            company: merchantSettings?.company,
          },
          {
            onError: (error: unknown) => {
              if (error instanceof Error) {
                if (
                  error.message.includes(paymentErrors.vatIdOrTaxNumberRequired)
                ) {
                  setIsCaptureTaxIDModalOpen(true);
                }
              }
            },
          }
        );
      }
    };

    eventBus.on(
      'ui.action.promote_down_payments_activated',
      handlePromotePayOnlineActivated
    );

    return () => {
      eventBus.off(
        'ui.action.promote_down_payments_activated',
        handlePromotePayOnlineActivated
      );
    };
  }, [eventBus, merchantSettings]);

  return (
    <Layout.Content style={{ padding: 45, height: 'max-content' }}>
      <Row gutter={0} align="middle">
        <Col span={14}>
          <Row>
            <Col span={24}>
              <Typography.Title
                level={1}
                style={{ width: '60rem', marginTop: 0, marginBottom: 30 }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'application-settings.payments.onboarding-page.introduction.title'
                    ),
                  }}
                />
              </Typography.Title>
              <Typography.Paragraph>
                {t(
                  'application-settings.payments.onboarding-page.introduction.first-description'
                )}
              </Typography.Paragraph>
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                {t(
                  'application-settings.payments.onboarding-page.introduction.second-description'
                )}
              </Typography.Paragraph>
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ margin: `45px 0` }}>
            <Col span={24}>
              <Row gutter={[16, 16]} style={{ maxWidth: '45rem' }}>
                <Col span={24} style={benefitListItemStyle}>
                  <Space direction="horizontal" size={15}>
                    <CloseCircleTwoTone style={{ fontSize: 28 }} />
                    <Typography.Text>
                      {t(
                        'application-settings.payments.onboarding-page.introduction.benefits.line1'
                      )}
                    </Typography.Text>
                  </Space>
                </Col>
                <Col span={24} style={benefitListItemStyle}>
                  <Space direction="horizontal" size={15}>
                    <TabletTwoTone style={{ fontSize: 28 }} />
                    <Typography.Text>
                      {t(
                        'application-settings.payments.onboarding-page.introduction.benefits.line2'
                      )}
                    </Typography.Text>
                  </Space>
                </Col>
                <Col span={24} style={benefitListItemStyle}>
                  <Space direction="horizontal" size={15}>
                    <DollarTwoTone style={{ fontSize: 28 }} />
                    <Typography.Text>
                      {t(
                        'application-settings.payments.onboarding-page.introduction.benefits.line3'
                      )}
                    </Typography.Text>
                  </Space>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row gutter={[16, 20]} style={{ marginTop: 20 }}>
            <Col span={24}>
              {isTrialMerchant && (
                <PaymentSetupTrialMerchantCTAButtons
                  onUpgradeContractClick={handleOnUpgradeContractClick}
                />
              )}
              {!isTrialMerchant && (
                <PaymentSetupPaidMerchantCTAButtons
                  isWhitelabel={isWhitelabel}
                  isLoadingPaypalSetup={isLoadingInitiatePayPalSetup}
                  isLoadingShorePayOnlineSetup={
                    isLoadingInitiateShorePayOnlineAccountSetup
                  }
                  isPayPalFeatureToggleEnabled={isPayPalFeatureToggleEnabled}
                  onLegacyStripeButtonClick={handleOnLegacyStripeButtonClick}
                  onShorePayOnlineAccountButtonClick={
                    handleOnShorePayOnlineAccountButtonClick
                  }
                  onPayPalButtonClick={handleOnPayPalButtonClick}
                />
              )}
              {isCaptureTaxIDModalOpen && (
                <PaymentCaptureTaxIDModal
                  isLoadingShorePayOnlineSetup={
                    isLoadingInitiateShorePayOnlineAccountSetup
                  }
                  OnClickClose={handleOnCaptureTaxIDModalClose}
                  onFormSubmit={handleOnCaptureTaxIDModalFormSubmit}
                />
              )}
            </Col>
          </Row>
        </Col>
        <Col span={10}>
          <Image
            alt="card holder"
            preview={false}
            src={`${CDN_BASE_URL}/images/holding-credit-card.png`}
            style={{ width: '100%' }}
          />
        </Col>
      </Row>
      <Row gutter={0} style={{ marginTop: 60 }}>
        <Col span={24}>
          <Row>
            <Col span={24}>
              <Typography.Text
                strong
                style={{
                  fontSize: 12,
                  color: '#00d0be',
                  textTransform: 'uppercase',
                }}
              >
                {t(
                  'application-settings.payments.onboarding-page.advantages.caption'
                )}
              </Typography.Text>
              <Typography.Title
                level={1}
                style={{ width: '60rem', marginTop: 0, marginBottom: 35 }}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'application-settings.payments.onboarding-page.advantages.title'
                    ),
                  }}
                />
              </Typography.Title>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row gutter={60}>
            <Col span={8}>
              <StyledAdvantageCard
                style={{
                  backgroundColor: '#acb0eb',
                }}
              >
                <StyledAdvantageCardIconContainer
                  style={{
                    backgroundColor: '#5860d6',
                  }}
                >
                  <CreditCardOutlined
                    style={{ fontSize: 24, color: '#ffffff' }}
                  />
                </StyledAdvantageCardIconContainer>
                <Typography.Title level={4}>
                  {t(
                    'application-settings.payments.onboarding-page.advantages.cards.card-first.title'
                  )}
                </Typography.Title>
                <Typography.Paragraph>
                  {t(
                    'application-settings.payments.onboarding-page.advantages.cards.card-first.content'
                  )}
                </Typography.Paragraph>
              </StyledAdvantageCard>
            </Col>
            <Col span={8}>
              <StyledAdvantageCard
                style={{
                  backgroundColor: '#b3f1ec',
                }}
              >
                <StyledAdvantageCardIconContainer
                  style={{
                    backgroundColor: '#00d0be',
                  }}
                >
                  <TeamOutlined style={{ fontSize: 24, color: '#ffffff' }} />
                </StyledAdvantageCardIconContainer>
                <Typography.Title level={4} style={{ color: '#00d0be' }}>
                  {t(
                    'application-settings.payments.onboarding-page.advantages.cards.card-second.title'
                  )}
                </Typography.Title>
                <Typography.Paragraph>
                  {t(
                    'application-settings.payments.onboarding-page.advantages.cards.card-second.content'
                  )}
                </Typography.Paragraph>
              </StyledAdvantageCard>
            </Col>
            <Col span={8}>
              <StyledAdvantageCard
                style={{
                  backgroundColor: '#ccebff',
                }}
              >
                <StyledAdvantageCardIconContainer
                  style={{
                    backgroundColor: '#09f',
                  }}
                >
                  <ScheduleOutlined
                    style={{ fontSize: 24, color: '#ffffff' }}
                  />
                </StyledAdvantageCardIconContainer>
                <Typography.Title level={4} style={{ color: '#09f' }}>
                  {t(
                    'application-settings.payments.onboarding-page.advantages.cards.card-third.title'
                  )}
                </Typography.Title>
                <Typography.Paragraph>
                  {t(
                    'application-settings.payments.onboarding-page.advantages.cards.card-third.content'
                  )}
                </Typography.Paragraph>
              </StyledAdvantageCard>
            </Col>
          </Row>
        </Col>
      </Row>
    </Layout.Content>
  );
}
